import { Box, Typography, useTheme } from "@mui/material";

/**
 * Illustrative charts for the onboarding interstitials. These are marketing
 * illustrations, not data plots — no axes or invented numbers, and every one
 * carries an "Illustrative" caption so nothing reads as a measured claim.
 * Flat UI: 2px strokes, solid tint fills, recessive grid, both themes.
 *
 * Rendered unframed (no border card) — the charts read as editorial, not app
 * furniture — and each path draws itself in on mount (pathLength normalizes
 * every path to length 1, so one keyframe drives them all).
 */

const CHART_GREEN = "#22c55e";

const gridColor = (isDark: boolean) =>
  isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)";

const mutedLineColor = (isDark: boolean) => (isDark ? "#6b7280" : "#9ca3af");

type ChartFrameProps = {
  caption: string;
  isDark: boolean;
  children: React.ReactNode;
};

const drawSx = {
  "@keyframes chartDraw": {
    from: { strokeDashoffset: 1 },
    to: { strokeDashoffset: 0 },
  },
  "@keyframes chartFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
  "@keyframes chartDotIn": {
    from: { opacity: 0, transform: "scale(0)" },
    to: { opacity: 1, transform: "scale(1)" },
  },
  "& .chart-draw": {
    strokeDasharray: 1,
    animation: "chartDraw 1.1s ease-out backwards",
  },
  "& .chart-fill": { animation: "chartFadeIn 0.4s ease 1s backwards" },
  "& .chart-dot": {
    transformBox: "fill-box" as const,
    transformOrigin: "center",
    animation: "chartDotIn 0.3s cubic-bezier(0.22, 1, 0.36, 1) 1s backwards",
  },
  "& .chart-label": { animation: "chartFadeIn 0.3s ease 1.2s backwards" },
  "@media (prefers-reduced-motion: reduce)": {
    "& .chart-draw, & .chart-fill, & .chart-dot, & .chart-label": {
      animation: "none",
    },
  },
} as const;

const ChartFrame = ({ caption, isDark, children }: ChartFrameProps) => (
  <Box sx={{ width: "100%", ...drawSx }}>
    {children}
    <Typography
      sx={{
        mt: 0.75,
        fontSize: "0.6875rem",
        textAlign: "center",
        color: isDark ? "#6b7280" : "#9ca3af",
      }}
    >
      {caption}
    </Typography>
  </Box>
);

const GridLines = ({ isDark }: { isDark: boolean }) => (
  <g stroke={gridColor(isDark)} strokeWidth="1">
    <line x1="0" y1="40" x2="300" y2="40" />
    <line x1="0" y1="80" x2="300" y2="80" />
    <line x1="0" y1="120" x2="300" y2="120" />
  </g>
);

/**
 * Single rising curve — the compounding of consistent, informed decisions.
 * One series, so the caption names it and no legend is needed.
 */
export const CompoundingChart = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <ChartFrame isDark={isDark} caption="Illustrative: consistent decisions compound over time">
      <svg
        viewBox="0 0 300 140"
        width="100%"
        role="img"
        aria-label="Illustrative chart of a value compounding upward over time"
      >
        <GridLines isDark={isDark} />
        <path
          className="chart-draw"
          pathLength={1}
          d="M8 124 C 90 118, 160 104, 210 76 C 245 56, 272 34, 292 16"
          fill="none"
          stroke={CHART_GREEN}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          className="chart-fill"
          d="M8 124 C 90 118, 160 104, 210 76 C 245 56, 272 34, 292 16 L 292 132 L 8 132 Z"
          fill="rgba(34, 197, 94, 0.08)"
        />
        <circle className="chart-dot" cx="292" cy="16" r="4" fill={CHART_GREEN} />
      </svg>
    </ChartFrame>
  );
};

/**
 * Two diverging paths from the same starting point: research-driven (green,
 * steady climb) vs guesswork (muted, erratic drift). Identity is carried by
 * direct labels with colored dots — never color alone.
 */
export const ComparisonChart = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const muted = mutedLineColor(isDark);
  const labelColor = isDark ? "#e5e7eb" : "#374151";
  return (
    <ChartFrame isDark={isDark} caption="Illustrative: the research gap, not a measured return">
      <svg
        viewBox="0 0 300 140"
        width="100%"
        role="img"
        aria-label="Illustrative chart comparing a steady research-driven path against an erratic guesswork path"
      >
        <GridLines isDark={isDark} />
        <path
          className="chart-draw"
          pathLength={1}
          d="M8 100 C 60 96, 110 84, 165 64 C 205 50, 240 40, 268 30"
          fill="none"
          stroke={CHART_GREEN}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle className="chart-dot" cx="268" cy="30" r="4" fill={CHART_GREEN} />
        {/* Guesswork baseline: hovers just above the floor with barely any
            growth — flat, not a decline (a downtrend read as "you'll lose
            money", which isn't the claim). */}
        <path
          className="chart-draw"
          pathLength={1}
          style={{ animationDuration: "1.4s" }}
          d="M8 100 C 40 98, 60 104, 95 100 C 130 97, 145 103, 185 99 C 215 97, 240 97, 268 94"
          fill="none"
          stroke={muted}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle className="chart-dot" cx="268" cy="94" r="3.5" fill={muted} />
        <g className="chart-label" fontSize="11" fontWeight="600" fontFamily="inherit">
          <text x="262" y="18" textAnchor="end" fill={labelColor}>
            With research
          </text>
          <text x="262" y="114" textAnchor="end" fill={labelColor}>
            On tips alone
          </text>
        </g>
      </svg>
    </ChartFrame>
  );
};
